import React from 'react';
import PropTypes from 'prop-types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { clsx } from 'clsx';

import { BusinessReviewHorizontal } from '../../atoms/business-reviews';

import { titleProp, datoGatsbyImageProp } from '../../../types';
import { createHeadingHighlight } from '../../../utils/heading-highlight';

import * as headingStyles from './index.module.css';
import { CallUs } from '../../atoms/call-us-cta-bdm';

const MainHeader = ({
	id,
	header,
	secondaryHeader,
	as,
	primaryImage,
	secondaryImage,
	primaryLinkCopy,
	googleReview,
	showTrustPilot: showBusinessReview,
	headingClassNames,
	glu,
	logo,
	bdmName,
	bdmNumber,
}) => {
	const Component = as;
	const titleText = createHeadingHighlight({
		headings: header[0].children,
		headingStyles,
	});
	const primaryImageData = getImage(primaryImage);
	const secondaryImageData = getImage(secondaryImage);

	return (
		<section
			role="banner"
			className={clsx('px-4 pt-10 pb-6 lg:py-6 mx-auto lg:pr-0', {
				'bg-brand-pale-400': glu,
				'lg:max-w-m-screen': !glu,
			})}
			aria-labelledby={id}
		>
			<div
				className={clsx('flex mx-auto', {
					'lg:max-w-m-screen': glu,
				})}
			>
				<div
					className={clsx(
						'flex flex-col justify-center shrink lg:mr-8 lg:pl-8 xl:mr-12 lg:-mt-16',
						headingStyles.headingContainer
					)}
				>
					{logo && (
						<img
							src={logo.url}
							alt={logo.alt || ''}
							className="max-w-[275px]"
						/>
					)}
					<Component
						id={id}
						className={clsx(
							'!leading-[1.3] font-lexend-regular',
							!secondaryHeader ? headingStyles.heading : null,
							glu
								? 'text-2xl lg:text-4xl font-bold lg:min-h-[160px]'
								: 'text-4xl md:text-4xl xl:text-5xl md:w-8/12 xl:w-10/12',
							headingClassNames
						)}
					>
						{titleText}
					</Component>

					<span
						className={clsx(
							'mt-2 leading-[1.2] block',
							'md:mt-2',
							' xl:leading-[1.3]',

							glu
								? 'text-xl lg:text-2xl font-lexend-regular'
								: `text-2xl md:text-3xl ${headingStyles.heading} `
						)}
					>
						{secondaryHeader}
					</span>

					{showBusinessReview ? (
						<BusinessReviewHorizontal googleReview={googleReview} />
					) : null}
					<p className="flex flex-col items-center justify-center mt-8 sm:flex-row sm:justify-start">
						<CallUs
							className="inline-flex items-center px-6 py-4 mb-2 mr-2 text-lg leading-6 text-white transition-colors bg-blue-400 border-2 border-blue-400 rounded-lg md:mb-0 hover:bg-brand-blue-400 hover:border-brand-blue-400 focus:bg-brand-blue-500 md:px-8 md:text-xl font-lexend-medium focus:outline-0 focus:shadow-focus w-max"
							number={bdmNumber}
							primaryLinkCopy={primaryLinkCopy}
						/>
					</p>
				</div>
				<div
					className={clsx(
						'hidden',
						'grid-cols-12',
						'lg:grid',
						'grow',
						'shrink-0',
						'w-[36%]',
						headingStyles.imageContainer
					)}
				>
					<GatsbyImage
						image={primaryImageData}
						alt={primaryImage.alt || ''}
						key={primaryImage.url}
						className={clsx(
							'border-8',
							'border-white',
							'drop-shadow-image',
							headingStyles.image1,
							'2xl:min-w-[400px]'
						)}
					/>
					<GatsbyImage
						image={secondaryImageData}
						alt={secondaryImage.alt || ''}
						key={secondaryImage.url}
						className={clsx(
							'border-8',
							'border-white',
							'drop-shadow-image',
							headingStyles.image2,
							'2xl:min-w-[330px]'
						)}
					/>
				</div>
			</div>

			<div className="hidden grid-cols-6 lg:grid">
				<div className="col-span-4" />
				<div className="ml-4 text-center">{bdmName}</div>
			</div>
		</section>
	);
};

MainHeader.defaultProps = {
	id: 'main-site-header',
	as: 'h1',
	secondaryHeader: '',
	headingClassNames: '',
	showTrustPilot: true,
	glu: false,
	logo: {},
	bdmName: '',
	bdmNumber: '',
};

MainHeader.propTypes = {
	id: PropTypes.string,
	header: titleProp.isRequired,
	secondaryHeader: PropTypes.string,
	as: PropTypes.string,
	primaryImage: datoGatsbyImageProp.isRequired,
	secondaryImage: datoGatsbyImageProp.isRequired,
	headingClassNames: PropTypes.string,
	primaryLinkCopy: PropTypes.string.isRequired,
	showTrustPilot: PropTypes.bool,
	glu: PropTypes.bool,
	logo: datoGatsbyImageProp,
	bdmName: PropTypes.string,
	bdmNumber: PropTypes.string,
	googleReview: PropTypes.shape({
		url: PropTypes.string.isRequired,
		alt: PropTypes.string.isRequired,
	}).isRequired,
};

export { MainHeader };
